import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import {
  tokens,
  fmtNumber,
  SignalBar,
  StatusPill,
  CodeChip,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import AdherenceDetailPanel from "../components/AdherenceDetailPanel";
import { getAdherenceQueue } from "../services/adherenceService";
import {
  LOCATIONS,
  DEFAULT_LOCATION,
  PDC_THRESHOLD,
  pdcTone,
} from "../common/constants";

const COLUMNS = [
  {
    key: "pdc",
    header: "PDC",
    align: "right",
    mono: true,
    width: 65,
    render: (r) => (
      <Box component="span" sx={{ color: tokens[pdcTone(r.pdc)], fontWeight: 600 }}>
        {r.pdc}%
      </Box>
    ),
  },
  { key: "name", header: "Patient" },
  {
    key: "therapeuticClass",
    header: "Class",
    render: (r) => <CodeChip code={r.therapeuticClass} />,
  },
  { key: "medications", header: "Meds", align: "right", mono: true, width: 60 },
  {
    key: "recentLabs",
    header: "Labs",
    width: 70,
    render: (r) =>
      r.recentLabs === "Y" ? (
        <StatusPill tone="ok" label="Recent" dense />
      ) : (
        <StatusPill tone="warn" label="None" dense />
      ),
  },
  {
    key: "lastVisit",
    header: "Last visit",
    render: (r) => (
      <Box
        component="span"
        sx={{
          fontSize: 12.5,
          color: /9 Months|12\+/.test(r.lastVisit || "") ? tokens.crit : tokens.ink2,
        }}
      >
        {r.lastVisit}
      </Box>
    ),
  },
];

/**
 * Module C — Adherence Board (Medication Adherence Manager).
 * Cola de polifarmacia/adherencia ordenada por PDC ascendente, con detalle
 * de régimen, flags y acciones de outreach.
 */
export default function MedicationAdherencePage() {
  const [locationKey, setLocationKey] = useState(DEFAULT_LOCATION);
  const [data, setData] = useState({ stats: {}, patients: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getAdherenceQueue(locationKey)
      .then((d) => {
        if (!active) return;
        setData(d);
        setSelectedId(d.patients[0]?.cipId ?? null);
      })
      .catch(() => {
        if (!active) return;
        setData({ stats: {}, patients: [] });
        setSelectedId(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [locationKey]);

  const selectedRow = useMemo(
    () => data.patients.find((p) => p.cipId === selectedId) || null,
    [data, selectedId]
  );

  const signals = useMemo(() => {
    const rows = data.patients;
    const total = rows.length;
    const below = rows.filter((r) => r.pdc < PDC_THRESHOLD).length;
    const refillGaps = rows.reduce((sum, r) => sum + r.refillGaps, 0);
    const avgPdc = total
      ? Math.round(rows.reduce((sum, r) => sum + r.pdc, 0) / total)
      : 0;
    const noLabs = data.stats.withoutRecentLabs ?? rows.filter((r) => r.recentLabs === "N").length;
    return [
      { label: "Polypharmacy 5+", value: fmtNumber(total), tone: "info" },
      {
        label: "Avg PDC",
        value: `${avgPdc}%`,
        tone: pdcTone(avgPdc),
        hint: "Average proportion of days covered across the queue (simulated)",
      },
      {
        label: `Below ${PDC_THRESHOLD}%`,
        value: below,
        tone: below > 0 ? "crit" : "ok",
      },
      { label: "Open refill gaps", value: refillGaps, tone: refillGaps > 0 ? "warn" : "ok" },
      { label: "No recent labs", value: noLabs, tone: noLabs > 0 ? "warn" : "ok" },
      {
        label: "8+ medications",
        value: data.stats.totalWith8Plus ?? 0,
        tone: "muted",
      },
    ];
  }, [data]);

  const locationLabel =
    LOCATIONS.find((l) => l.key === locationKey)?.label || locationKey;

  return (
    <ClinicalPageShell
      title="Adherence Board"
      subtitle="Polypharmacy and medication-adherence queue, sorted by lowest PDC. Detect refill gaps, review barriers, and trigger outreach before measures slip."
      moduleTag="Module C"
      devPreview
      actions={
        <Select
          size="small"
          value={locationKey}
          onChange={(e) => setLocationKey(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170, bgcolor: tokens.surface }}
        >
          {LOCATIONS.map((l) => (
            <MenuItem key={l.key} value={l.key} sx={{ fontSize: 13 }}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "45%" }, flexShrink: 0 }}>
          <ClinicalDataGrid
            columns={COLUMNS}
            rows={data.patients}
            rowKey={(r) => r.cipId}
            loading={loading}
            onRowClick={(r) => setSelectedId(r.cipId)}
            isRowSelected={(r) => r.cipId === selectedId}
            emptyMessage="No polypharmacy patients for this location."
            maxHeight={620}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <AdherenceDetailPanel
            key={selectedRow?.cipId ?? "none"}
            row={selectedRow}
            locationLabel={locationLabel}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
